import {
  ServiceCodeGroupType,
  type IChartedTreatment,
  type IFeeSchedule,
  type IPricedServiceCodeEntry,
  type IPricedServiceCodeGroup,
  type IScopedServiceCode,
  type IServiceCodeEntry,
  type ITreatmentConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import { isSameIdentifier, type WithRef } from '@principle-theorem/shared';
import { differenceBy } from 'lodash';
import { ServiceCodeGroup } from '../service-codes/ada-code-category';
import { TreatmentConfiguration } from './treatment-configuration';

export class ScopedGroupTranslator {
  static convertScopedGroupCodes(
    treatment: IChartedTreatment,
    feeSchedule: WithRef<IFeeSchedule>,
    config?: WithRef<ITreatmentConfiguration>
  ): IChartedTreatment {
    if (!config || !TreatmentConfiguration.hasScopedGroups(config)) {
      return treatment;
    }

    return {
      ...treatment,
      serviceCodes: ScopedGroupTranslator._convertServiceCodes(
        treatment,
        feeSchedule,
        config
      ),
      serviceCodeGroups: ScopedGroupTranslator._convertExclusiveGroups(
        treatment,
        feeSchedule,
        config
      ),
    };
  }

  private static _convertServiceCodes(
    treatment: IChartedTreatment,
    feeSchedule: WithRef<IFeeSchedule>,
    config: WithRef<ITreatmentConfiguration>
  ): IPricedServiceCodeEntry[] {
    const requiredCodes = TreatmentConfiguration.getCombinedServiceCodes(
      config,
      [ServiceCodeGroupType.Required],
      feeSchedule
    );
    const codesToRemove = differenceBy(
      ScopedGroupTranslator._codesForOtherSchedules(config, feeSchedule),
      requiredCodes,
      scopedServiceCodeKey
    );
    const remainingCodes = differenceBy(
      treatment.serviceCodes,
      codesToRemove,
      scopedServiceCodeKey
    );
    const missingRequiredCodes = differenceBy(
      requiredCodes,
      remainingCodes,
      scopedServiceCodeKey
    ).map((serviceCode) => ({
      ...serviceCode,
      chartedSurfaces: treatment.chartedSurfaces,
    }));

    return [...remainingCodes, ...missingRequiredCodes];
  }

  private static _codesForOtherSchedules(
    config: WithRef<ITreatmentConfiguration>,
    feeSchedule: WithRef<IFeeSchedule>
  ): IServiceCodeEntry[] {
    return config.serviceCodeGroups
      .filter(
        (group) =>
          !ServiceCodeGroup.appliesToScheduleType(
            group,
            feeSchedule.serviceCodeType
          )
      )
      .flatMap((group) => group.serviceCodes);
  }

  private static _convertExclusiveGroups(
    treatment: IChartedTreatment,
    feeSchedule: WithRef<IFeeSchedule>,
    config: WithRef<ITreatmentConfiguration>
  ): IPricedServiceCodeGroup[] {
    return TreatmentConfiguration.getServiceGroups(
      config,
      [ServiceCodeGroupType.Exclusive],
      feeSchedule
    ).map(
      (exclusiveGroup) =>
        treatment.serviceCodeGroups.find((existing) =>
          isSameIdentifier(existing, exclusiveGroup)
        ) ?? exclusiveGroup
    );
  }
}

function scopedServiceCodeKey(serviceCode: IScopedServiceCode): string {
  return `${serviceCode.type}-${serviceCode.code}`;
}
